
import React, { useState, useMemo } from 'react';
import { Product, Order, OrderStatus, Dupe } from '../types';
import { BarChart3, Package, ShoppingCart, LogOut, Edit3, XCircle, ListChecks, CheckCircle2, AlertTriangle, Trash2, PlusCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

interface AdminProps {
  products: Product[];
  orders: Order[];
  dupes: Dupe[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  setDupes: React.Dispatch<React.SetStateAction<Dupe[]>>;
  onLogout: () => void;
  monthlyGoal: number;
  setMonthlyGoal: (goal: number) => void;
}

const PREDEFINED_LABELS = ['Aucun', 'Best-seller', 'Promo', 'Nouveau', 'Édition Limitée', 'Rupture'];
const INITIAL_CATEGORIES = ['Brumes Parfumées', 'Lave-mains', 'Gels Antiseptiques', 'Crèmes Mains', 'Room Sprays'];

const Admin: React.FC<AdminProps> = ({ products, orders, dupes, setProducts, setOrders, setDupes, onLogout, monthlyGoal, setMonthlyGoal }) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'orders' | 'dupes'>('dashboard');
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isEditingDupe, setIsEditingDupe] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDupe, setIsSavingDupe] = useState(false);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const navigate = useNavigate();

  const availableCategories = useMemo(() => {
    const fromProducts = (products || []).map(p => p.category);
    return Array.from(new Set([...INITIAL_CATEGORIES, ...fromProducts])).filter(Boolean).sort();
  }, [products]);

  const totalRevenue = useMemo(() => {
    return (orders || [])
      .filter(order => order?.status === 'livrée')
      .reduce((acc, curr) => acc + (Number(curr?.total) || 0), 0);
  }, [orders]);
  
  const goalProgress = useMemo(() => {
    if (!monthlyGoal || monthlyGoal === 0) return 0;
    return Math.round((totalRevenue / monthlyGoal) * 100);
  }, [totalRevenue, monthlyGoal]);

  const [formData, setFormData] = useState<Partial<Product>>({
    name: '', price: 0, category: INITIAL_CATEGORIES[0], label: 'Aucun', image: '', hoverImage: '', stock: 0, isOutOfStock: false
  });

  const [dupeFormData, setDupeFormData] = useState<Partial<Dupe>>({
    title: '', image: ''
  });

  const updateOrderStatus = async (orderId: string, newStatus: OrderStatus) => {
    try {
      const targetOrder = (orders || []).find(o => o.id === orderId);
      if (!targetOrder) return;

      const previousStatus = targetOrder.status;
      if (previousStatus === newStatus) return;

      const shouldRestoreStock = previousStatus !== 'annulée' && newStatus === 'annulée';
      const shouldReserveStockAgain = previousStatus === 'annulée' && newStatus !== 'annulée';

      if (shouldRestoreStock || shouldReserveStockAgain) {
        const direction = shouldRestoreStock ? 1 : -1;
        const items = safeItems(targetOrder.items);
        const quantityByProductId = new Map<string, number>();

        for (const item of items) {
          if (!item?.id) continue;
          const current = quantityByProductId.get(item.id) || 0;
          quantityByProductId.set(item.id, current + (Number(item.quantity) || 0));
        }

        const nextProducts = [...(products || [])];

        for (const [productId, qty] of quantityByProductId.entries()) {
          const pIdx = nextProducts.findIndex(p => p.id === productId);
          if (pIdx < 0) continue;

          const product = nextProducts[pIdx];
          const nextStock = Math.max(0, (Number(product.stock) || 0) + (qty * direction));

          const { error: stockError } = await supabase
            .from('products')
            .update({ stock: nextStock, isOutOfStock: nextStock <= 0 })
            .eq('id', productId);

          if (stockError) throw stockError;

          nextProducts[pIdx] = { ...product, stock: nextStock, isOutOfStock: nextStock <= 0 };
        }

        setProducts(nextProducts);
      }

      const { error } = await supabase.from('orders').update({ status: newStatus }).eq('id', orderId);
      if (error) throw error;
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (err) {
      console.error("Erreur mise à jour statut:", err);
    }
  };

  const deleteOrder = async (id: string) => {
    if (!confirm("Supprimer cette commande ?")) return;
    try {
      const { error } = await supabase.from('orders').delete().eq('id', id);
      if (error) throw error;
      setOrders(prev => prev.filter(o => o.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const categoryToSave = isAddingNewCategory ? newCategoryName.trim() : formData.category;
    if (!formData.name || formData.price === undefined || formData.price === null || !formData.image || !categoryToSave) {
      alert("Informations manquantes.");
      return;
    }

    setIsSaving(true);
    const normalizedStock = Number(formData.stock) || 0;
    const productPayload = {
      name: formData.name,
      price: Number(formData.price),
      category: categoryToSave,
      stock: normalizedStock,
      label: formData.label === 'Aucun' ? null : formData.label,
      isOutOfStock: normalizedStock <= 0,
      image: formData.image,
      hoverImage: formData.hoverImage || formData.image,
    };

    try {
      if (isEditing) {
        const { error: updateError } = await supabase
          .from('products')
          .update(productPayload)
          .eq('id', isEditing);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('products')
          .insert([{ ...productPayload, id: `rc-${Date.now()}` }]);
        if (insertError) throw insertError;
      }

      const { data, error: refreshError } = await supabase.from('products').select('*');
      if (refreshError) throw refreshError;
      if (data) setProducts(data);
      resetForm();
    } catch (err) {
      console.error("Erreur sauvegarde produit:", err);
      alert("Impossible de sauvegarder ce produit. Vérifiez les permissions Supabase (RLS) et la console.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDupe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dupeFormData.title || !dupeFormData.image) {
      alert("Informations manquantes.");
      return;
    }

    setIsSavingDupe(true);
    const dupePayload = {
      title: dupeFormData.title,
      image: dupeFormData.image,
    };

    try {
      if (isEditingDupe) {
        await supabase.from('dupes').update(dupePayload).eq('id', isEditingDupe);
      } else {
        await supabase.from('dupes').insert([{ ...dupePayload, id: `dupe-${Date.now()}` }]);
      }
      const { data } = await supabase.from('dupes').select('*').order('created_at', { ascending: false });
      if (data) setDupes(data);
      resetDupeForm();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSavingDupe(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', price: 0, category: availableCategories[0] || INITIAL_CATEGORIES[0], label: 'Aucun', image: '', hoverImage: '', stock: 0 });
    setIsEditing(null);
    setIsAddingNewCategory(false);
    setNewCategoryName('');
  };

  const resetDupeForm = () => {
    setDupeFormData({ title: '', image: '' });
    setIsEditingDupe(null);
  };

  const deleteDupe = async (id: string) => {
    if (!confirm("Supprimer cette correspondance ?")) return;
    try {
      const { error } = await supabase.from('dupes').delete().eq('id', id);
      if (error) throw error;
      setDupes(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusStyle = (status: OrderStatus) => {
    switch (status) {
      case 'en cours': return 'bg-brand-gold/10 text-brand-gold border-brand-gold/20';
      case 'livrée': return 'bg-green-50 text-green-600 border-green-100';
      case 'annulée': return 'bg-red-50 text-red-500 border-red-100';
      default: return 'bg-brand-cream text-brand-black/40 border-brand-black/5';
    }
  };

  const safeItems = (items: any) => {
    if (!items) return [];
    if (Array.isArray(items)) return items;
    try {
      return JSON.parse(items);
    } catch {
      return [];
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-brand-offwhite pb-20 font-sans text-brand-black">
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center mb-10 border-b pb-8 border-brand-black/5">
          <div>
            <h1 className="font-serif text-3xl">Console <span className="text-brand-gold italic">Directoire</span></h1>
            <p className="text-[9px] uppercase tracking-[0.3em] font-bold opacity-30 mt-1 italic">R & C Essentials Management</p>
          </div>
          <button onClick={() => { onLogout(); navigate('/login'); }} className="text-[10px] uppercase tracking-widest font-bold opacity-40 hover:text-brand-gold luxury-transition flex items-center gap-2">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Navigation style Directoire */}
          <div className="lg:w-64 flex flex-row lg:flex-col gap-2">
            {[
              { id: 'dashboard', icon: BarChart3, label: 'Pilotage' },
              { id: 'products', icon: Package, label: 'Catalogue' },
              { id: 'orders', icon: ShoppingCart, label: 'Commandes' },
              { id: 'dupes', icon: Sparkles, label: 'Dupes' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 lg:flex-none flex items-center space-x-4 px-6 py-4 text-[10px] uppercase tracking-widest font-bold transition-all rounded-xl ${
                  activeTab === tab.id ? 'bg-brand-black text-brand-offwhite shadow-lg' : 'bg-brand-cream/30 text-brand-black/40 border border-brand-black/5'
                }`}
              >
                <tab.icon size={16}/>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 space-y-8">
            {activeTab === 'dashboard' && (
              <div className="space-y-8 animate-in fade-in duration-500">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-brand-cream/30 p-10 border border-brand-black/5 rounded-[2rem] flex flex-col justify-between h-48 shadow-sm">
                    <p className="text-[9px] uppercase tracking-widest text-brand-black/40 font-bold">CA Encaissé (Livrées)</p>
                    <p className="font-serif text-2xl text-brand-gold">{totalRevenue.toLocaleString()} FCFA</p>
                  </div>

                  <div className="bg-brand-cream/30 p-10 border border-brand-black/5 rounded-[2rem] flex flex-col justify-between h-48 shadow-sm">
                    <p className="text-[9px] uppercase tracking-widest text-brand-black/40 font-bold">Objectif Mensuel</p>
                    <input 
                      type="number" 
                      value={monthlyGoal} 
                      onChange={e => setMonthlyGoal(Number(e.target.value))} 
                      className="w-full font-serif text-xl outline-none bg-transparent border-b border-brand-black/10 text-brand-black font-bold" 
                    />
                    <div className="w-full bg-brand-black/5 h-1 rounded-full overflow-hidden">
                      <div className="bg-brand-gold h-full" style={{width: `${Math.min(100, goalProgress)}%`}}></div>
                    </div>
                  </div>

                  <div className="bg-brand-cream/30 p-10 border border-brand-black/5 rounded-[2rem] flex flex-col justify-between h-48 shadow-sm">
                    <p className="text-[9px] uppercase tracking-widest text-brand-black/40 font-bold">Stock Global</p>
                    <div className="font-serif text-2xl font-bold">
                      {(products || []).reduce((acc, p) => acc + (Number(p?.stock) || 0), 0)} unités
                    </div>
                    <div className="text-[8px] uppercase tracking-widest font-bold text-brand-gold">Inventaire Actif</div>
                  </div>
                </div>

                <div className="bg-brand-cream/30 p-8 border border-brand-black/5 rounded-[2rem] shadow-sm">
                  <div className="flex items-center gap-3 mb-8">
                    <ListChecks className="text-brand-gold" size={20} />
                    <h3 className="font-serif text-xl">État de l'Inventaire</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-brand-black/5">
                          <th className="pb-4 text-[10px] uppercase tracking-widest font-bold text-brand-black/40">Article</th>
                          <th className="pb-4 text-[10px] uppercase tracking-widest font-bold text-brand-black/40 text-center">Stock</th>
                          <th className="pb-4 text-[10px] uppercase tracking-widest font-bold text-brand-black/40 text-right">Disponibilité</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(products || []).map(p => {
                          const s = Number(p?.stock) || 0;
                          return (
                            <tr key={p?.id} className="border-b border-brand-black/5 last:border-0 hover:bg-brand-cream/40 transition-colors">
                              <td className="py-5 flex items-center gap-4">
                                <div className="w-10 h-12 bg-white rounded-lg flex items-center justify-center p-1 border border-brand-black/5 shadow-sm">
                                  <img src={p?.image} className="w-full h-full object-contain" alt=""/>
                                </div>
                                <span className={`text-sm font-medium ${s <= 0 ? 'text-brand-black/30' : 'text-brand-black'}`}>{p?.name}</span>
                              </td>
                              <td className="py-5 text-center font-bold">
                                <span className={`px-4 py-1 rounded-full text-[10px] ${s <= 0 ? 'text-red-600 bg-red-50' : 'text-green-700 bg-green-50'}`}>
                                  {s}
                                </span>
                              </td>
                              <td className="py-5 text-right">
                                 {s <= 0 ? (
                                   <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest flex items-center justify-end gap-1"><AlertTriangle size={12}/> ÉPUISÉ</span>
                                 ) : (
                                   <span className="text-[9px] font-bold text-green-600 uppercase tracking-widest flex items-center justify-end gap-1"><CheckCircle2 size={12}/> EN STOCK</span>
                                 )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="space-y-12 animate-in fade-in duration-500">
                <form onSubmit={handleSave} className="bg-brand-cream/30 p-10 border border-brand-black/5 rounded-[2rem] shadow-xl relative">
                  {isEditing && (
                    <button type="button" onClick={resetForm} className="absolute top-8 right-8 text-brand-black/40 hover:text-red-500 flex items-center gap-2 text-[9px] uppercase font-bold">
                      <XCircle size={16} /> Annuler
                    </button>
                  )}
                  <h3 className="font-serif text-3xl mb-12 text-brand-black">{isEditing ? 'Éditer l\'article' : 'Nouvelle Référence'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="space-y-8">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black/40 block mb-3">Nom commercial</label>
                        <input className="w-full p-5 border border-brand-black/10 bg-white rounded-xl text-sm outline-none focus:border-brand-gold text-brand-black" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                      </div>
                      <div className="grid grid-cols-2 gap-8">
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black/40 block mb-3">Prix (FCFA)</label>
                          <input type="number" className="w-full p-5 border border-brand-black/10 bg-white rounded-xl text-sm font-bold text-brand-black" value={formData.price || ''} onChange={e => setFormData({...formData, price: Number(e.target.value)})} />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-brand-gold block mb-3 italic">Stock</label>
                          <input type="number" className="w-full p-5 border-2 border-brand-gold bg-white rounded-xl text-sm font-bold text-brand-black" value={formData.stock} onChange={e => setFormData({...formData, stock: Number(e.target.value)})} />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black/40 block mb-3">Catégorie</label>
                          {isAddingNewCategory ? (
                            <div className="flex gap-2">
                              <input 
                                className="flex-1 p-4 border border-brand-gold bg-white rounded-xl text-xs outline-none text-brand-black" 
                                placeholder="Nom..."
                                value={newCategoryName} 
                                onChange={e => setNewCategoryName(e.target.value)} 
                              />
                              <button type="button" onClick={() => setIsAddingNewCategory(false)} className="p-2 text-brand-black/40"><XCircle size={18}/></button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <select className="flex-1 p-5 border border-brand-black/10 bg-white rounded-xl text-[10px] font-bold text-brand-black" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                                {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                              </select>
                              <button type="button" onClick={() => setIsAddingNewCategory(true)} className="p-4 bg-brand-black text-white rounded-xl hover:bg-brand-gold transition-colors"><PlusCircle size={18}/></button>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black/40 block mb-3">Tag / Badge</label>
                          <select className="w-full p-5 border border-brand-black/10 bg-white rounded-xl text-[10px] font-bold text-brand-black" value={formData.label || 'Aucun'} onChange={e => setFormData({...formData, label: e.target.value})}>
                            {PREDEFINED_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-8 text-center">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black/40 block mb-4">Visuel</label>
                      <label className="aspect-[3/4] border-2 border-dashed border-brand-gold/20 bg-white rounded-[2rem] flex flex-col items-center justify-center cursor-pointer hover:bg-brand-gold/5 luxury-transition relative overflow-hidden group shadow-inner">
                        {formData.image ? <img src={formData.image} className="w-full h-full object-contain p-4" alt=""/> : <div className="text-center opacity-30"><Package size={40} className="mx-auto mb-2"/><span className="text-[8px] uppercase font-black">Uploader</span></div>}
                        <input type="file" className="hidden" accept="image/*" onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => setFormData(prev => ({ ...prev, image: reader.result as string }));
                            reader.readAsDataURL(file);
                          }
                        }} />
                      </label>
                    </div>
                  </div>
                  <button type="submit" disabled={isSaving} className="w-full bg-brand-black text-brand-offwhite py-6 rounded-xl text-[10px] uppercase tracking-[0.5em] font-bold hover:bg-brand-gold transition-all mt-10 shadow-2xl active:scale-[0.99]">{isSaving ? 'Chargement...' : 'Enregistrer le rituel'}</button>
                </form>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                   {(products || []).map(p => (
                     <div key={p?.id} className="bg-brand-cream/30 p-6 border border-brand-black/5 rounded-[1.5rem] flex items-center justify-between group hover:border-brand-gold transition-all shadow-sm">
                        <div className="flex items-center gap-5">
                           <div className="w-12 h-16 bg-white rounded-lg flex items-center justify-center p-1 border border-brand-black/5">
                             <img src={p?.image} className="w-full h-full object-contain" alt=""/>
                           </div>
                           <div>
                              <h5 className="font-serif text-lg leading-tight text-brand-black">{p?.name}</h5>
                              <p className="text-[9px] font-bold uppercase tracking-widest text-brand-gold mt-1">{p?.price?.toLocaleString()} FCFA</p>
                           </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => {
                            setIsEditing(p?.id);
                            setFormData({ ...p, stock: Number(p?.stock) || 0 });
                          }} className="p-2 text-brand-black/20 hover:text-brand-gold transition-colors"><Edit3 size={18} /></button>
                          <button onClick={async () => { if(confirm("Supprimer ?")) { await supabase.from('products').delete().eq('id', p?.id); setProducts(prev => prev.filter(item => item.id !== p?.id)); } }} className="p-2 text-brand-black/10 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                        </div>
                     </div>
                   ))}
                </div>
              </div>
            )}
            
            {activeTab === 'orders' && (
               <div className="space-y-8 animate-in fade-in duration-500">
                 {(!orders || orders.length === 0) ? (
                   <div className="text-center py-32 bg-brand-cream/20 rounded-[2rem] border border-brand-black/5">
                      <p className="text-[10px] uppercase tracking-widest font-bold opacity-30 italic text-brand-black">Aucune commande enregistrée.</p>
                   </div>
                 ) : (
                   orders.map(o => (
                     <div key={o?.id} className="bg-brand-cream/30 border border-brand-black/5 rounded-[2rem] shadow-sm hover:border-brand-gold/30 transition-all overflow-hidden">
                        <div className="p-8 md:p-10 flex flex-col md:flex-row justify-between gap-6 border-b border-brand-black/5">
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-gold">COMMANDE #{String(o?.id || '').slice(-6).toUpperCase()}</p>
                              <span className={`text-[8px] uppercase font-bold px-3 py-1 border rounded-full ${getStatusStyle(o?.status)}`}>
                                {o?.status || 'Inconnu'}
                              </span>
                            </div>
                            <h4 className="font-serif text-3xl mb-1 text-brand-black">{o?.customerName || 'Client Anonyme'}</h4>
                            <p className="text-[10px] text-brand-black/40 font-medium tracking-wider">{o?.customerPhone || 'Sans numéro'}</p>
                            <p className="text-[9px] text-brand-gold font-bold uppercase tracking-widest mt-2 italic">Règlement: {o?.paymentMethod || 'Non spécifié'}</p>
                          </div>
                          <div className="text-right">
                             <p className="font-serif text-3xl text-brand-black">{(o?.total || 0).toLocaleString()} FCFA</p>
                             <p className="text-[9px] text-brand-black/30 uppercase tracking-widest mt-2">{safeItems(o?.items).length} article(s) au panier</p>
                          </div>
                        </div>

                        <div className="p-8 md:p-10 bg-brand-offwhite/40 space-y-6">
                          <p className="text-[9px] uppercase tracking-widest font-bold text-brand-black/30 mb-2">Contenu du rituel</p>
                          <div className="grid grid-cols-1 gap-4">
                            {safeItems(o?.items).map((item: any, idx: number) => (
                              <div key={`${o?.id}-item-${idx}`} className="flex items-center justify-between border-b border-brand-black/5 pb-4 last:border-0">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-16 bg-white rounded-lg flex items-center justify-center p-1 border border-brand-black/5 shadow-sm">
                                    <img 
                                      src={item?.selectedVariant?.image || item?.image} 
                                      className="w-full h-full object-contain" 
                                      alt=""
                                    />
                                  </div>
                                  <div>
                                    <p className="text-xs font-bold text-brand-black uppercase tracking-wider">{item?.name || 'Produit inconnu'}</p>
                                    {item?.selectedVariant && (
                                      <p className="text-[9px] uppercase tracking-widest text-brand-gold font-bold">{item?.selectedVariant?.name}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs font-black text-brand-black">x{item?.quantity || 1}</p>
                                  <p className="text-[10px] text-brand-black/30 tracking-tight">{(item?.price * (item?.quantity || 1))?.toLocaleString()} FCFA</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="px-10 py-6 border-t border-brand-black/5 flex flex-wrap items-center justify-between gap-4">
                           <div className="flex items-center gap-3">
                             {(['en cours', 'livrée', 'annulée'] as OrderStatus[]).map(s => (
                               <button 
                                 key={s} 
                                 onClick={() => updateOrderStatus(o?.id, s)} 
                                 className={`text-[8px] uppercase font-bold px-5 py-2 transition-all rounded-full border ${o?.status === s ? 'bg-brand-black text-brand-offwhite border-brand-black shadow-md' : 'bg-transparent text-brand-black/30 border-brand-black/10 hover:border-brand-gold hover:text-brand-gold'}`}
                               >
                                 {s}
                               </button>
                             ))}
                           </div>
                           <button onClick={() => deleteOrder(o?.id)} className="p-4 text-brand-black/10 hover:text-red-500 transition-all"><Trash2 size={20} /></button>
                        </div>
                     </div>
                   ))
                 )}
               </div>
            )}

            {activeTab === 'dupes' && (
              <div className="space-y-12 animate-in fade-in duration-500">
                <form onSubmit={handleSaveDupe} className="bg-brand-cream/30 p-10 border border-brand-black/5 rounded-[2rem] shadow-xl relative">
                  {isEditingDupe && (
                    <button type="button" onClick={resetDupeForm} className="absolute top-8 right-8 text-brand-black/40 hover:text-red-500 flex items-center gap-2 text-[9px] uppercase font-bold">
                      <XCircle size={16} /> Annuler
                    </button>
                  )}
                  <h3 className="font-serif text-3xl mb-12 text-brand-black">{isEditingDupe ? 'Éditer la correspondance' : 'Nouvelle Correspondance'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="space-y-8">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black/40 block mb-3">Titre / Nom</label>
                        <input className="w-full p-5 border border-brand-black/10 bg-white rounded-xl text-sm outline-none focus:border-brand-gold text-brand-black" value={dupeFormData.title} onChange={e => setDupeFormData({...dupeFormData, title: e.target.value})} />
                      </div>
                    </div>
                    <div className="space-y-8 text-center">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black/40 block mb-4">Image de correspondance</label>
                      <label className="aspect-[4/5] border-2 border-dashed border-brand-gold/20 bg-white rounded-[2rem] flex flex-col items-center justify-center cursor-pointer hover:bg-brand-gold/5 luxury-transition relative overflow-hidden group shadow-inner">
                        {dupeFormData.image ? <img src={dupeFormData.image} className="w-full h-full object-contain p-4" alt=""/> : <div className="text-center opacity-30"><Sparkles size={40} className="mx-auto mb-2"/><span className="text-[8px] uppercase font-black">Uploader</span></div>}
                        <input type="file" className="hidden" accept="image/*" onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => setDupeFormData(prev => ({ ...prev, image: reader.result as string }));
                            reader.readAsDataURL(file);
                          }
                        }} />
                      </label>
                    </div>
                  </div>
                  <button type="submit" disabled={isSavingDupe} className="w-full bg-brand-black text-brand-offwhite py-6 rounded-xl text-[10px] uppercase tracking-[0.5em] font-bold hover:bg-brand-gold transition-all mt-10 shadow-2xl active:scale-[0.99]">{isSavingDupe ? 'Chargement...' : 'Enregistrer la correspondance'}</button>
                </form>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                   {(dupes || []).map(d => (
                     <div key={d?.id} className="bg-brand-cream/30 p-6 border border-brand-black/5 rounded-[1.5rem] flex items-center justify-between group hover:border-brand-gold transition-all shadow-sm">
                        <div className="flex items-center gap-5">
                           <div className="w-12 h-16 bg-white rounded-lg flex items-center justify-center p-1 border border-brand-black/5">
                             <img src={d?.image} className="w-full h-full object-contain" alt=""/>
                           </div>
                           <div>
                              <h5 className="font-serif text-lg leading-tight text-brand-black">{d?.title}</h5>
                           </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setIsEditingDupe(d?.id); setDupeFormData(d); }} className="p-2 text-brand-black/20 hover:text-brand-gold transition-colors"><Edit3 size={18} /></button>
                          <button onClick={() => deleteDupe(d?.id)} className="p-2 text-brand-black/10 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                        </div>
                     </div>
                   ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
